import { ErrorMessage, Field, Formik, Form as FormikForm } from 'formik';
import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import Pessoa, { Vinculo } from '../../../model/Pessoa';
import PessoaViewModel from '../../../viewmodel/PessoaViewModel';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import './PessoaCreateModal.css';

interface PessoaCreateModalProps {
    show: boolean;
    handleClose: () => void;
    onCreate: (pessoa: Pessoa) => void;
    vinculos: Vinculo[];
}

const PessoaCreateModal: React.FC<PessoaCreateModalProps> = ({ show, handleClose, onCreate, vinculos }) => {

    const initialValues: Omit<Pessoa, 'id'> = {
        nome: '',
        cpf: '',
        vinculo: { id: null, nome: 'Selecione um Vínculo' },
        email: '',
        matricula: '',
        telefone: '',
        isMembroCavu: true,
    };

    const validationSchema = Yup.object().shape({
        nome: Yup.string().required('Nome é obrigatório'),
        cpf: Yup.string()
            .matches(/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, 'CPF inválido. Use o formato 123.456.789-00 ou apenas números')
            .required('CPF é obrigatório'),
        vinculo: Yup.object().shape({
            nome: Yup.string()
                .required('Vínculo é obrigatório')
                .test('not-default', 'Selecione um Vínculo válido', value => value !== 'Selecione um Vínculo'),
        }),
        email: Yup.string().email('Email inválido').required('Email é obrigatório'),
        matricula: Yup.string().nullable(),
        telefone: Yup.string()
            .matches(
                /^(\+\d{1,3})?\s?(\(\d{2}\)|\d{2})?\s?\d{4,5}-?\d{4}$/,
                'Telefone inválido. Use (XX) XXXX-XXXX ou (XX) XXXXX-XXXX'
            )
            .nullable(),
    });

    const formatCPF = (cpf: string) => {
        cpf = cpf.replace(/\D/g, '');
        return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    };

    const isCPFFormatted = (cpf: string) => {
        const cpfPattern = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
        return cpfPattern.test(cpf);
    };

    const handleSubmit = async (values: Omit<Pessoa, 'id'>) => {
        try {
            // Verifica se o CPF já está formatado
            if (!isCPFFormatted(values.cpf)) {
                // Aplica a máscara de CPF se não estiver formatado
                values.cpf = formatCPF(values.cpf);
            }

            const response = await PessoaViewModel.createPessoa(values);

            if (response) {
                toast.success('Pessoa criada com sucesso!');
                onCreate(response);
                handleClose();
            } else {
                throw new Error('Falha ao criar pessoa. Tente novamente.');
            }
        } catch (error) {
            console.error('Erro ao criar pessoa:', error);
            toast.error(BackendErrorMessage(error));
        }
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Adicionar Membro CAVU</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ setFieldValue }) => (
                        <FormikForm>
                            <div className="p-3 mb-2 bg-light rounded shadow-sm">
                                <Form.Group controlId="formNome">
                                    <Form.Label>Nome</Form.Label>
                                    <Field type="text" name="nome" className="form-control" />
                                    <ErrorMessage name="nome" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="formNomeSocial">
                                    <Form.Label>Nome Social</Form.Label>
                                    <Field type="text" name="nomeSocial" className="form-control" />
                                    <ErrorMessage name="nomeSocial" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="formCpf">
                                    <Form.Label>
                                        <div style={{ display: 'flex' }}>
                                            CPF
                                        </div>
                                    </Form.Label>
                                    <Field type="text" name="cpf" className="form-control" />
                                    <ErrorMessage name="cpf" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="formPassaporte">
                                    <Form.Label>Passaporte</Form.Label>
                                    <Field type="text" name="passaporte" className="form-control" />
                                    <ErrorMessage name="passaporte" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="formEmail">
                                    <Form.Label>Email</Form.Label>
                                    <Field type="email" name="email" className="form-control" />
                                    <ErrorMessage name="email" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="formVinculo">
                                    <Form.Label>Vínculo</Form.Label>
                                    <Field
                                        as="select"
                                        name="vinculo.nome"
                                        className="form-control"
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            const selectedVinculo = vinculos.find(v => v.nome === e.target.value);
                                            if (selectedVinculo) {
                                                setFieldValue('vinculo.id', selectedVinculo.id);
                                                setFieldValue('vinculo.nome', selectedVinculo.nome);
                                            } else {
                                                setFieldValue('vinculo.id', null);
                                                setFieldValue('vinculo.nome', '');
                                            }
                                        }}
                                    >
                                        <option value="">Selecione um vínculo</option>
                                        {vinculos.map(vinculo => (
                                            <option key={vinculo.id} value={vinculo.nome}>
                                                {vinculo.nome}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="vinculo.nome" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="formMatricula">
                                    <Form.Label>Matrícula</Form.Label>
                                    <Field type="text" name="matricula" className="form-control" />
                                    <ErrorMessage name="matricula" component="div" className="text-danger" />
                                </Form.Group>
                                <Form.Group controlId="formTelefone">
                                    <Form.Label>Telefone</Form.Label>
                                    <Field
                                        type="text"
                                        name="telefone"
                                        className="form-control"
                                        placeholder="(XX) XXXXX-XXXX"
                                    />
                                    <ErrorMessage name="telefone" component="div" className="text-danger" />
                                </Form.Group>
                                <Button variant="primary" type="submit">
                                    Adicionar Membro CAVU
                                </Button>
                            </div>
                        </FormikForm>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    );
};

export default PessoaCreateModal;